"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, UserCheck, ChartBar as BarChart3, PhoneCall, Award, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
export default function LocalHeroTrust() {
  const trustPoints = [{
    title: "Direct Consultant Access",
    description: "You work directly with me, Yassine Essaadi. No account managers, delegates, or junior agency hand-offs.",
    icon: UserCheck
  }, {
    title: "Tailored Local Strategy",
    description: "Custom local search campaigns designed for Cheltenham, Gloucestershire, and surrounding regional markets.",
    icon: BarChart3
  }, {
    title: "Transparent ROI Reporting",
    description: "Clear deliverables, monthly video updates, and honest tracking focused on tangible leads and business growth.",
    icon: ShieldCheck
  }];
  const localCapabilities = ["Google Map Pack & Local Pack Top Rankings", "High-Intent Cheltenham Customer Targeting", "Google Business Profile Optimisation", "Localized Content & Authority Link Building"];
  return <section id="localherotrust" data-nav="dark" className="relative w-full bg-primary text-primary-foreground py-16 md:py-20 lg:py-28 overflow-hidden">
      {/* Decorative Blur Backgrounds */}
      <div className="absolute top-0 right-0 -mt-12 -mr-12 size-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-12 -ml-12 size-96 rounded-full bg-secondary/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Heading & Value Proposition */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            
            {/* Location Pill */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 backdrop-blur-sm text-xs md:text-sm font-medium">
              <MapPin className="size-4 text-accent" />
              <span>Independent Local SEO Specialist in Cheltenham</span>
              <Badge variant="secondary" className="ml-1 bg-accent text-accent-foreground font-semibold">
                Gloucestershire
              </Badge>
            </div>

            {/* Main H1 Heading */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-primary-foreground">
              Local SEO Consultant in Cheltenham
            </h2>

            {/* Subheading / Value Copy */}
            <p className="text-base sm:text-lg md:text-xl text-primary-foreground/90 font-sans leading-relaxed max-w-2xl">
              I help Cheltenham and regional Gloucestershire businesses outrank competitors, dominate local map packs, and drive consistent local customer leads. When you hire me, you work directly with me — no agency bloat, no delegation.
            </p>

            {/* Direct Action CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-7 py-3.5 text-base rounded-lg shadow-lg transition-transform active:scale-95">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-semibold px-7 py-3.5 text-base rounded-lg backdrop-blur-xs">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>

            {/* Quick Deliverables Checklist */}
            <div className="pt-4 border-t border-primary-foreground/15 w-full grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-primary-foreground/85">
              {localCapabilities.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-4 shrink-0 text-accent" />
                  <span>{item}</span>
                </div>)}
            </div>

          </div>

          {/* Right Column: Founder Authority Card */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-end">
            <div className="w-full max-w-md bg-card text-card-foreground rounded-2xl p-6 sm:p-8 shadow-2xl border border-border/80 relative">
              
              {/* Floating Badge */}
              <div className="absolute -top-3.5 left-6 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 shadow-sm">
                <Sparkles className="size-3.5" />
                <span>100% Direct Access</span>
              </div>

              {/* Founder Header */}
              <div className="flex items-center gap-4 mb-6 pt-2">
                <SafeHydrate><Avatar className="size-16 border-2 border-primary shadow-md shrink-0">
                  <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp" alt="Yassine Essaadi - SEO Consultant" />
                  <AvatarFallback className="bg-primary text-primary-foreground font-bold">YE</AvatarFallback>
                </Avatar></SafeHydrate>
                <div>
                  <h2 className="font-serif text-xl font-bold text-card-foreground">Yassine Essaadi</h2>
                  <p className="text-sm text-muted-foreground font-medium">Independent SEO Strategy Consultant</p>
                  <div className="flex items-center gap-1.5 text-xs text-primary font-semibold mt-1">
                    <Award className="size-3.5" />
                    <span>SEO Specialist &amp; Founder</span>
                  </div>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Founder Guarantee Statement */}
              <div className="space-y-3 mb-6">
                <p className="text-sm text-muted-foreground leading-relaxed italic">
                  &ldquo;I handle your local SEO audit, strategy, and execution personally. You receive clear communication, custom execution, and real business results.&rdquo;
                </p>
              </div>

              {/* Direct Reach Contacts */}
              <div className="bg-muted/60 rounded-xl p-4 space-y-3 border border-border/50 text-xs">
                <div className="flex items-center justify-between text-card-foreground font-medium">
                  <span className="text-muted-foreground">Location Focus:</span>
                  <span className="font-semibold text-primary">Cheltenham &amp; Regional UK</span>
                </div>
                <div className="flex items-center justify-between text-card-foreground font-medium">
                  <span className="text-muted-foreground">Direct Email:</span>
                  <a href="mailto:contact@ye-consultancy.com" className="font-semibold text-primary hover:underline">
                    contact@ye-consultancy.com
                  </a>
                </div>
                <div className="flex items-center justify-between text-card-foreground font-medium">
                  <span className="text-muted-foreground">Direct Phone:</span>
                  <a href="tel:+447490984430" className="font-semibold text-primary hover:underline">
                    +44 7490984430
                  </a>
                </div>
              </div>

              {/* Founder Direct Action */}
              <div className="mt-6">
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-3 rounded-lg">
                  <Link to="/ContactPage" className="flex items-center justify-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Speak Directly With Yassine</span>
                  </Link>
                </Button>
              </div>

            </div>
          </div>

        </div>

        {/* Bottom Trust Band */}
        <div className="mt-16 pt-12 border-t border-primary-foreground/15 grid grid-cols-1 md:grid-cols-3 gap-8">
          {trustPoints.map((point, i) => {
          const IconComponent = point.icon;
          return <div key={i} data-index={i} className="flex gap-4 items-start">
                <div className="p-3 rounded-xl bg-primary-foreground/10 text-accent border border-primary-foreground/15 shrink-0">
                  <IconComponent className="size-6" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-serif text-lg font-semibold text-primary-foreground">
                    {point.title}
                  </h3>
                  <p className="text-sm text-primary-foreground/80 leading-relaxed">
                    {point.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

      </div>
    </section>;
}